/* eslint-disable react/prop-types */
import { Box, Typography, Chip } from "@mui/material";
import CustomTable, { CustomTableColumn } from "components/common/CustomTable";
import dayjs from "dayjs";
import { TransferResponseDto } from "libs/cellera-sdk/models";
import { useCallback, useMemo } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";

interface CustomTransferTableProps {
  transfers: TransferResponseDto[];
  isLoading: boolean;
  currentPage: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  pageSize: number;
}

const CustomTransferTable: React.FC<CustomTransferTableProps> = ({
  transfers,
  isLoading,
  currentPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  pageSize,
}) => {
  const { colors } = useCelleraTheme();

  const getStatusColor = useCallback(
    (status: string) => {
      switch (status) {
        case "in_transit":
          return colors.background.highlight;
        case "paid":
          return colors.success[100];
        case "pending":
          return colors.warning[100];
        case "failed":
          return colors.error[100];
        default:
          return colors.background.grayLight;
      }
    },
    [colors]
  );

  const getStatusTextColor = useCallback(
    (status: string) => {
      switch (status) {
        case "in_transit":
          return colors.primary.main;
        case "paid":
          return colors.primary.main;
        case "pending":
          return colors.primary.main;
        case "failed":
          return colors.primary.main;
        default:
          return colors.text.secondary;
      }
    },
    [colors]
  );

  const columns: CustomTableColumn<TransferResponseDto>[] = useMemo(
    () => [
      {
        id: "stripeTransferId",
        label: "Transfer ID",
        getValue: (row) => (
          <Typography variant="body2" color={colors.text.secondary}>
            {row.stripeTransferId}
          </Typography>
        ),
        width: "25%",
      },
      {
        id: "amount",
        label: "Amount",
        getValue: (row) => (
          <Typography
            variant="body2"
            sx={{ color: colors.text.secondary, fontWeight: 600 }}
          >
            ${row.amountInDollars}
          </Typography>
        ),
        width: "15%",
        align: "right",
      },
      {
        id: "description",
        label: "Description",
        getValue: (row) => (
          <Typography variant="body2" sx={{ color: colors.text.secondary }}>
            {row.description || "No description"}
          </Typography>
        ),
        width: "35%",
      },
      {
        id: "status",
        label: "Status",
        getValue: (row) => (
          <Chip
            label={row.status.replace("_", " ").toUpperCase()}
            size="small"
            sx={{
              backgroundColor: getStatusColor(row.status),
              color: getStatusTextColor(row.status),
              fontWeight: 600,
              fontSize: "0.75rem",
            }}
          />
        ),
        width: "15%",
        align: "center",
      },
      {
        id: "createdAt",
        label: "Date",
        getValue: (row) => (
          <Typography variant="body2" sx={{ color: colors.text.secondary }}>
            {dayjs(row.createdAt).format("MMM DD, YYYY HH:mm")}
          </Typography>
        ),
        width: "20%",
      },
    ],
    [colors, getStatusColor, getStatusTextColor]
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <Typography variant="h6" sx={{ mb: 0 }}>
        Transfer History
      </Typography>
      <CustomTable
        data={transfers}
        columns={columns}
        totalCount={totalCount}
        page={currentPage - 1} // Convert to 0-based indexing for CustomTable
        rowsPerPage={pageSize}
        onPageChange={(_, newPage) => onPageChange(newPage + 1)} // Convert back to 1-based indexing
        onRowsPerPageChange={onRowsPerPageChange}
        isLoading={isLoading}
        getRowKey={(row) => row.id}
        emptyStateContent={
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No transfers found
            </Typography>
          </Box>
        }
      />
    </Box>
  );
};

export default CustomTransferTable;
